"use client"

import * as React from "react"
import { usePathname, useRouter } from "next/navigation"
// Sprint 137 (Founder directive 2026-09-22) — Site Maintenance Mode.
// Hook polls /api/site-status + redirects non-admin users to /maintenance
// when the admin flips FF_SITE_MAINTENANCE_MODE on. Admins are exempt.
import { useSiteMaintenance } from "@/hooks/use-site-maintenance"
import { toast } from "sonner"
// PR #1586 (CRITICAL fix): import tokenStore so we can guard the
// session-expired redirect against a stale flag. Without this guard,
// a stale `window.__profileia.sessionExpired` flag (set by a PRIOR
// refresh failure but cleared by a LATER successful refresh — see
// fetch-client.ts tryRefresh) caused an infinite redirect loop
// /app/dashboard ↔ /login on every AppShell mount.
import { tokenStore } from "@/lib/api-client"

/**
 * useAppShell — encapsulates the AppShell component's state + effects +
 * handlers (extracted VERBATIM from the original app-shell.tsx by SPEC-244
 * §Sprint5 IA-18 — pure hook, no JSX).
 *
 * Effects owned by this hook:
 *  1. `profileia:session-expired` listener — toast + redirect to
 *     /login?reason=session_expired (M-18 + BUG-EXP5-012 + P-109).
 *  2. Site-maintenance redirect — polls /api/site-status + redirects
 *     non-admin users to /maintenance (Sprint 137, P-350 fix).
 *
 * The view (`app-shell/index.tsx`) calls this hook + renders the static
 * SidebarProvider > AppSidebar + TopBar + main-content layout — it does
 * NOT consume any return value (both effects are side-effect only).
 *
 * §6 Anti-Invención: cero cambios de lógica — same effects, same deps,
 * same handlers as the original inline implementation.
 */
export function useAppShell(): void {
  const router = useRouter()

  // M-18 + BUG-EXP5-012 (SPEC-041 QA audit pre-launch): listen for the
  // `profileia:session-expired` CustomEvent (dispatched by `apiFetch` when
  // a 401 + failed refresh is detected) instead of polling
  // `window.__profileia.sessionExpired` every 2s. The poll had three
  // problems:
  //   (a) Latency — up to 2s between the 401 and the toast.
  //   (b) CPU waste — constant property lookup + setState conditional.
  //   (c) Race — if the flag was set and cleared between two polls
  //       (e.g., a refresh succeeded right after a 401), the toast
  //       never appeared.
  // The CustomEvent fires immediately when apiFetch detects the 401, so
  // the toast shows within ms and the race is eliminated.
  //
  // BUG-EXP5-026: use the typed `window.__profileia` (declared in
  // `types/global.d.ts`) directly — no more `(window as any).__profileia`
  // cast. The typed declaration was added in M-18 (R2 SPEC-037) but the
  // cast was left in place out of caution. It's now safe to remove.
  React.useEffect(() => {
    const handler = () => {
      if (typeof window === 'undefined') return;
      // P-109 (SPEC-090 §1.6): redirect IMMEDIATELY to
      // /login?reason=session_expired — do NOT clear the flag first
      // (the redirect unmounts this AppShell, the flag will be reset
      // by the new login page after navigation completes). The previous
      // implementation only showed a toast + cleared the flag, which
      // left the user on an auth-gated page with `user=null` — the
      // sidebar's "Usuario" fallback rendered the misleading "U
      // Usuario Networker · Gratuito" state for 30+ seconds until the
      // user manually clicked "Iniciar sesión" in the toast action.
      //
      // The toast is shown BEFORE the redirect so it survives the SPA
      // navigation (sonner toasts live in a portal outside React's
      // tree + persist across route changes). The redirect uses
      // `router.replace` (not `window.location.href`) so the user
      // doesn't lose other in-memory state (e.g., the dashboard
      // search query) on a full page reload.
      toast.error('Sesión expirada', {
        description: 'Tu sesión ha expirado. Por favor inicia sesión de nuevo.',
        duration: 10000,
      });
      router.replace('/login?reason=session_expired');
    };
    // Check the flag once on mount (covers the race where the 401 happened
    // before this AppShell mounted — e.g., during initial hydration).
    // PR #1586 (CRITICAL fix): ONLY redirect if there is NO valid access
    // token in tokenStore. The `sessionExpired` flag can be stale — set by
    // a PRIOR refresh failure but cleared by a LATER successful refresh
    // (see fetch-client.ts tryRefresh). Without the `!tokenStore.getAccess()`
    // guard, every AppShell mount would redirect to /login even when the
    // user has a perfectly valid access token → infinite redirect loop
    // /app/dashboard ↔ /login (screen flickering, dashboard enters/exits
    // non-stop — Founder report 2026-11-02).
    if (typeof window !== 'undefined' && window.__profileia?.sessionExpired && !tokenStore.getAccess()) {
      handler();
    }
    window.addEventListener('profileia:session-expired', handler);
    return () => window.removeEventListener('profileia:session-expired', handler);
  }, [router])

  // Sprint 137 (Founder directive 2026-09-22) — Site Maintenance Mode.
  // Polls /api/site-status + redirects non-admin users to /maintenance
  // when the admin flips FF_SITE_MAINTENANCE_MODE on. Admins (role
  // admin/superadmin/it) are exempt (they can browse + operate normally
  // while the site is in maintenance mode — the backend middleware
  // ALSO exempts them, defense-in-depth). The hook polls every 60s +
  // on window focus + on visibilitychange. Fail-closed: if the fetch
  // fails, `maintenance` is false (site operational). The backend
  // middleware is the AUTHORITATIVE gate — this redirect is a UX
  // affordance so the user sees the maintenance page proactively
  // without waiting for a failed API call.
  //
  // Anti-P-043: AbortController cancels the in-flight request on unmount.
  // §6 Anti-Invención: pure data-fetching hook — no business logic.
  const pathname = usePathname();
  const { maintenance } = useSiteMaintenance();
  React.useEffect(() => {
    // Sprint 139 R2 (P-350 fix): CRITICAL — admin must ALWAYS be able to
    // reach /admin/* routes even when maintenance is ON. Pre-fix: the
    // redirect to /maintenance happened for ALL non-admin users,
    // INCLUDING not-yet-logged-in admins (user.role is null before
    // login → isAdmin=false → redirect fires → admin can't reach
    // /admin/login → admin is LOCKED OUT → "cómo voy a desactivar el
    // mantenimiento si no tengo acceso" (Founder 2026-09-26).
    //
    // Post-fix: check if the current path starts with /admin — if so,
    // NEVER redirect to /maintenance. The admin login page at
    // /admin/login will render normally, and the backend exempts
    // /api/auth/login + /api/admin/* so the admin can log in + toggle
    // the flag off.
    const isAdminPath = pathname.startsWith('/admin');
    if (maintenance && pathname !== '/maintenance' && !isAdminPath) {
      // Use window.location.href (NOT router.replace) so the page does a
      // full reload — the AppShell unmounts + the /maintenance page
      // renders as a standalone static page (no sidebar, no TopBar).
      // This also re-runs useSiteMaintenance on the maintenance page
      // (the page itself doesn't use the hook, but the redirect-back
      // logic is the "Reintentar" button = full reload to / which
      // re-fetches /api/site-status + redirects back to /app if
      // maintenance is now off).
      if (typeof window !== 'undefined') {
        window.location.href = '/maintenance';
      }
    }
  }, [maintenance, pathname]);
}
